import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import InventoryGrid from "@/components/InventoryGrid";
import PodNetworkSection from "@/components/PodNetworkSection";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1768776183877-e8f3dfc91f40?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxvdXRkb29yJTIwcGFydHklMjBjZWxlYnJhdGlvbnxlbnwwfHx8fDE3NzcxMzg2MTF8MA&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const [items, setItems] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const cart = useCart();

  useEffect(() => {
    const params = new URLSearchParams({
      start_date: cart.startDate,
      days: String(cart.rentalDays),
    });
    api
      .get(`/inventory?${params.toString()}`)
      .then(({ data }) => setItems(data))
      .catch(() => {});
  }, [cart.startDate, cart.rentalDays]);

  const visible = showAll ? items : items.filter((i) => i.category === "Standard");

  const startDateLabel = new Date(cart.startDate + "T00:00:00").toLocaleDateString(
    "en-US",
    { weekday: "short", month: "short", day: "numeric" }
  );

  return (
    <div className="bg-brand-slate min-h-screen">
      <Navbar />

      {/* HERO */}
      <header className="relative h-[640px] md:h-[700px] flex items-center justify-center overflow-hidden">
        <img
          src={HERO_IMAGE}
          alt="Park Hill event setup"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          className="absolute inset-0 bg-[#0D2240]"
          style={{ opacity: 0.85, mixBlendMode: "multiply" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0D2240] via-transparent to-transparent" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center" data-testid="hero">
          <span className="bg-brand-red text-white text-[10px] font-black uppercase tracking-[0.3em] px-6 py-2 rounded-full mb-8 inline-block border border-brand-gold">
            Denver&apos;s Self-Serve Rental Network
          </span>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-black mb-6 leading-none tracking-tightest text-white">
            RENT. UNLOCK. <br />
            <span className="text-brand-gold uppercase italic">CELEBRATE.</span>
          </h1>
          <p className="text-lg md:text-2xl text-slate-200 max-w-3xl mx-auto mb-10 font-bold leading-relaxed">
            A growing network of self-serve rental pods across Denver.
            <br />
            <span className="text-white/90">Find the pod nearest you. Unlock with your phone.</span>
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-4">
            <a
              href="#pods"
              className="btn-primary px-10 md:px-14 py-5 rounded-2xl font-black text-base md:text-xl inline-flex items-center justify-center gap-3 shadow-2xl uppercase tracking-widest"
              data-testid="hero-find-pod-cta"
            >
              <i className="fas fa-location-dot" />
              Find Your Pod
            </a>
            <Link
              to="/register"
              className="bg-white/10 backdrop-blur-md text-white border border-white/20 px-10 py-5 rounded-2xl font-black text-base md:text-lg inline-flex items-center justify-center gap-3 uppercase tracking-widest hover:bg-white/20 transition-colors"
              data-testid="hero-signup-cta"
            >
              <i className="fas fa-user-plus" />
              Create Account
            </Link>
          </div>
        </div>
      </header>

      {/* PROCESS */}
      <section id="process" className="py-20 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-3">
            How It Works
          </p>
          <h2 className="text-3xl md:text-5xl font-black mb-14 uppercase tracking-tightest text-brand-blue">
            Express Pick-Up Process
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { n: "1", title: "Find", body: "Locate the pod nearest you across our Denver network.", border: "border-brand-gold", icon: "fa-location-dot" },
              { n: "2", title: "Book", body: "Reserve gear online and receive a 6-digit unlock PIN.", border: "border-brand-red", icon: "fa-key" },
              { n: "3", title: "Party", body: "Unlock the pod, grab your gear, celebrate, and return it.", border: "border-brand-blue", icon: "fa-bolt" },
            ].map((s) => (
              <div
                key={s.n}
                className={`p-8 rounded-[32px] bg-slate-50 border-b-8 ${s.border} text-left hover:shadow-lg transition-shadow`}
                data-testid={`process-step-${s.n}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="w-14 h-14 bg-brand-blue rounded-2xl flex items-center justify-center text-2xl text-brand-gold font-black shadow-lg">
                    {s.n}
                  </div>
                  <i className={`fas ${s.icon} text-3xl text-slate-300`} />
                </div>
                <h3 className="text-2xl font-black mb-2 text-brand-blue uppercase tracking-tightest">
                  {s.title}
                </h3>
                <p className="text-slate-600 font-medium text-sm leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POD NETWORK — nearest-first cards */}
      <PodNetworkSection />

      {/* INVENTORY */}
      <section id="inventory" className="py-24 bg-brand-slate">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between md:items-end mb-12 gap-4">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.4em] text-brand-red mb-2">
                Available Across the Network
              </p>
              <h2 className="text-4xl md:text-5xl font-black uppercase tracking-tightest text-brand-blue leading-none">
                Rental <span className="text-brand-red">Catalog</span>
              </h2>
              <p className="text-slate-500 font-bold mt-3 text-xs uppercase tracking-widest underline underline-offset-4 decoration-brand-gold decoration-4">
                Tap any item • Customize • Reserve from your nearest pod
              </p>
            </div>
            <button
              onClick={() => setShowAll((v) => !v)}
              className="px-6 py-3 bg-white border border-slate-200 text-brand-blue font-black uppercase rounded-full hover:bg-brand-gold hover:border-brand-gold transition-colors text-[10px] tracking-widest self-start"
              data-testid="toggle-view-all-button"
            >
              {showAll ? "Show Standard Only" : "View All Items"}
              <i className={`fas ${showAll ? "fa-chevron-up" : "fa-chevron-down"} ml-2`} />
            </button>
          </div>

          {/* Availability banner */}
          <div
            className="bg-brand-blue text-white rounded-[24px] px-5 py-4 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b-4 border-brand-gold"
            data-testid="inventory-availability-banner"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-brand-gold text-brand-blue flex items-center justify-center">
                <i className="fas fa-bolt" />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-brand-gold">
                  Showing Availability For
                </p>
                <p className="font-black text-base md:text-lg uppercase tracking-tightest">
                  {startDateLabel} • {cart.rentalDays} day{cart.rentalDays > 1 ? "s" : ""}
                </p>
              </div>
            </div>
            <button
              onClick={() => cart.setOpen(true)}
              className="bg-white/10 hover:bg-white/20 transition-colors px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border border-white/20"
              data-testid="inventory-change-date-button"
            >
              Change Date <i className="fas fa-calendar ml-2" />
            </button>
          </div>

          <InventoryGrid items={visible} />
        </div>
      </section>

      <Footer />
    </div>
  );
}
